import { useEffect, useMemo, useState } from "react";
import { Main } from "@/components/Main";
import { PageHeader } from "@/components/PageHeader";
import { CreateLoaderTable } from "@/components/Loaders";
import { CalendarRange, ChevronLeft, ChevronRight } from "lucide-react";
import { getVacationCalendar } from "@/Api/absences";
import { getSelectSectors } from "@/Api/sectors";
import { cn } from "@/lib/utils";
import { Pagination } from "@/components/Pagination";
import { UnityFilter, useUnityFilter } from "@/components/UnityFilter";
import { AbsenceStatusConst } from "@/types/absence";
import { RequestAbsenceBtn } from "@/App/Absences/Request";
import { If } from "@/components/If";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const MONTH_LABELS = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

const formatDay = (isoDate: string) => isoDate.slice(8, 10);

/**
 * Meses (0-11) que o período do abono toca dentro do ano exibido — um abono
 * de férias pode atravessar a virada do ano, então só entram os meses que
 * caem dentro do `year` pedido.
 */
const monthsTouched = (startDate: string, endDate: string, year: number) => {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  const months: number[] = [];

  for (let month = 0; month < 12; month++) {
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);
    if (start <= monthEnd && end >= monthStart) {
      months.push(month);
    }
  }

  return months;
};

const PER_PAGE = 20;

export const PageList = () => {
  const { hasPermission } = usePermissions();
  const [year, setYear] = useState(new Date().getFullYear());
  const unityFilter = useUnityFilter();
  const unityId = unityFilter.param ?? "";
  const [sectors, setSectors] = useState<any[]>([]);
  const [sectorId, setSectorId] = useState<string>("");
  const [employees, setEmployees] = useState<any[]>([]);
  const [state, setState] = useState("loading");

  const fetchCalendar = async () => {
    setState("loading");
    const { data } = await getVacationCalendar(year, {
      name: unityFilter.nameParam,
      unity_id: unityFilter.param,
      sector_id: sectorId && sectorId !== "all" ? Number(sectorId) : undefined,
    });
    setEmployees(data?.response?.employees ?? []);
    setState("production");
  };

  // Setores são cadastrados por unidade: a lista acompanha a unidade filtrada.
  useEffect(() => {
    setSectorId("");
    (async () => {
      const { data: sectorsData } = await getSelectSectors(unityFilter.param);
      setSectors(sectorsData?.response ?? []);
    })();
  }, [unityId]);

  useEffect(() => {
    if (!unityFilter.ready) return;
    fetchCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, unityFilter.ready, unityId, sectorId, unityFilter.nameParam]);

  const [page, setPage] = useState(1);

  const allRows = useMemo(
    () =>
      employees.map((employee) => ({
        ...employee,
        byMonth: MONTH_LABELS.map((_, month) =>
          employee.absences.filter((absence) =>
            monthsTouched(absence.start_date, absence.end_date, year).includes(month)
          )
        ),
      })),
    [employees, year]
  );

  const lastPage = Math.max(1, Math.ceil(allRows.length / PER_PAGE));
  const currentPage = Math.min(page, lastPage);
  const rows = useMemo(
    () => allRows.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE),
    [allRows, currentPage]
  );

  // Volta pra primeira página quando o recorte da lista muda.
  useEffect(() => {
    setPage(1);
  }, [year, unityId, sectorId, unityFilter.nameParam]);

  return (
    <Main>
      <If condition={hasPermission("view-absences-list")}>
        <PageHeader
          icon={CalendarRange}
          title="Planejamento de Férias"
          description="Visualize e programe as férias dos colaboradores ao longo do ano"
        />

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex items-center gap-1 rounded-md border bg-zinc-50 dark:bg-[#2c2c2c] px-2 py-1">
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              onClick={() => setYear((y) => y - 1)}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <span className="font-semibold w-14 text-center">{year}</span>
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              onClick={() => setYear((y) => y + 1)}
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>

          <div className="ml-auto">
            <RequestAbsenceBtn onCreated={fetchCalendar} onlyVacation />
          </div>
        </div>

        <UnityFilter
          filter={unityFilter}
          className="mb-4"
          namePlaceholder="Pesquisar colaborador"
        >
          <div className="space-y-1.5 w-full sm:w-60">
            <Label className="text-sm">Setor</Label>
            <Select value={sectorId} onValueChange={setSectorId}>
              <SelectTrigger className="h-9 w-full">
                <SelectValue placeholder="Todos os setores" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os setores</SelectItem>
                {sectors.map((sector: any) => (
                  <SelectItem key={sector.id} value={String(sector.id)}>
                    {sector.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </UnityFilter>

        {state === "loading" ? (
          <CreateLoaderTable cols={13} lines={8} />
        ) : (
          <div className="overflow-x-auto rounded border bg-zinc-50 dark:bg-[#2c2c2c]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 bg-zinc-50 dark:bg-[#2c2c2c] min-w-40">
                    Colaborador
                  </TableHead>
                  {MONTH_LABELS.map((label) => (
                    <TableHead key={label} className="text-center min-w-20">
                      {label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((employee, index) => (
                  <TableRow
                    key={employee.employee_id}
                    className={
                      index % 2 === 0
                        ? "bg-white dark:bg-[#242424] hover:bg-zinc-100 dark:hover:bg-[#303030]"
                        : "bg-zinc-100 dark:bg-[#2f2f2f] hover:bg-zinc-200/70 dark:hover:bg-[#383838]"
                    }
                  >
                    <TableCell
                      className={cn(
                        "sticky left-0 font-medium",
                        index % 2 === 0
                          ? "bg-white dark:bg-[#242424]"
                          : "bg-zinc-100 dark:bg-[#2f2f2f]"
                      )}
                    >
                      {employee.name}
                    </TableCell>
                    {employee.byMonth.map((absencesInMonth: any[], month: number) => (
                      <TableCell key={month} className="text-center align-top p-1">
                        <div className="flex flex-col gap-1 items-center">
                          {absencesInMonth.map((absence) => (
                            <span
                              key={absence.uuid}
                              title={`${absence.absence_type_name}: ${absence.start_date} a ${absence.end_date}`}
                              className={`text-xs rounded px-1.5 py-0.5 whitespace-nowrap ${
                                absence.status === AbsenceStatusConst.APPROVED
                                  ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-500/40"
                                  : "bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-dashed border-amber-500/40"
                              }`}
                            >
                              {formatDay(absence.start_date)}-{formatDay(absence.end_date)}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {rows.length < 1 && (
                  <TableRow>
                    <TableCell colSpan={13}>Nada foi encontrado.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {allRows.length > PER_PAGE && (
              <Pagination
                current_page={currentPage}
                last_page={lastPage}
                handleNext={() => setPage(currentPage + 1)}
                handlePrevious={() => setPage(currentPage - 1)}
                handleLastPage={() => setPage(lastPage)}
              />
            )}
          </div>
        )}
      </If>
    </Main>
  );
};

export default PageList;
